import type { NextRequest } from "next/server";
import { routing } from "./routing";

const localeSet = new Set<string>(routing.locales);

/**
 * For `localePrefix: "as-needed"`: strip a leading locale segment so route constants
 * (`/sign-in`, `/dashboard`, …) match. `localePrefix` is only non-empty for
 * non-default locales so redirects stay canonical (no `/en/...` when English omits the prefix).
 */
export function getLocalePathInfo(pathname: string): {
  locale: (typeof routing.locales)[number];
  pathnameWithoutLocale: string;
  localePrefix: string;
} {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];

  if (first && localeSet.has(first)) {
    const locale = first as (typeof routing.locales)[number];
    const rest = segments.slice(1);
    const localePrefix =
      locale === routing.defaultLocale ? "" : `/${locale}`;
    return {
      locale,
      pathnameWithoutLocale: rest.length > 0 ? `/${rest.join("/")}` : "/",
      localePrefix,
    };
  }

  return {
    locale: routing.defaultLocale,
    pathnameWithoutLocale:
      segments.length > 0 ? `/${segments.join("/")}` : "/",
    localePrefix: "",
  };
}

/**
 * `new URL("/sign-in", "https://host/es/foo")` drops `/es` — use this for redirects.
 */
export function localizedRedirectUrl(request: NextRequest, path: string): URL {
  const { localePrefix } = getLocalePathInfo(request.nextUrl.pathname);
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const combined =
    localePrefix === "" ? normalized : `${localePrefix}${normalized}`;
  return new URL(combined, request.url);
}
